import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Tenant } from '@/lib/types';
import { Banner, Card, Field, PageHeader, inputStyle, useToast } from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useAuth } from '@/store/auth';

const FRANCHISE = [
  { value: 'direct', label: '直營' },
  { value: 'franchise', label: '加盟' },
  { value: 'overseas', label: '海外' },
];

export function SettingsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN';
  const { msg, ok, fail } = useToast();

  const tenant = useQuery({ queryKey: ['tenant'], queryFn: () => api.get<Tenant>('/tenant') });
  const [f, setF] = useState<Tenant | null>(null);
  useEffect(() => { if (tenant.data) setF(tenant.data); }, [tenant.data]);

  const set = (k: keyof Tenant, v: string | number) => setF((s) => (s ? { ...s, [k]: v } : s));

  const save = useMutation({
    mutationFn: () =>
      api.patch('/tenant', {
        brand: f?.brand,
        color: f?.color,
        colorSoft: f?.colorSoft,
        colorTint: f?.colorTint,
        franchiseType: f?.franchiseType,
        royaltyPct: f?.royaltyPct,
      }),
    onSuccess: () => { ok('白牌設定已儲存並套用'); void qc.invalidateQueries({ queryKey: ['tenant'] }); },
    onError: fail,
  });

  const [line, setLine] = useState({ secret: '', token: '' });
  const saveLine = useMutation({
    mutationFn: () => api.patch('/tenant', { lineChannelSecret: line.secret, lineChannelToken: line.token }),
    onSuccess: () => { ok('LINE 頻道憑證已儲存'); setLine({ secret: '', token: '' }); void qc.invalidateQueries({ queryKey: ['tenant'] }); },
    onError: fail,
  });
  const webhookUrl = f ? `${window.location.origin}/api/v1/line/webhook/${f.id}` : '';

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get<BranchLite[]>('/catalog/branches'),
    enabled: isAdmin,
  });

  if (!f) return <div style={{ padding: 24, color: 'var(--ink-3)' }}>載入中…</div>;

  return (
    <div>
      <PageHeader title="白牌 / 租戶設定" subtitle="品牌名稱、主色系、加盟模式 —— 儲存後即時套用全站主題" />
      <Banner msg={msg} />

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <Card title="品牌設定" icon={<Icon d={I.settings} size={14} style={{ color: 'var(--primary)' }} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="品牌名稱"><input style={inputStyle} value={f.brand} disabled={!isAdmin} onChange={(e) => set('brand', e.target.value)} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <ColorField label="主色" value={f.color} disabled={!isAdmin} onChange={(v) => set('color', v)} />
              <ColorField label="柔色" value={f.colorSoft} disabled={!isAdmin} onChange={(v) => set('colorSoft', v)} />
              <ColorField label="淺底" value={f.colorTint} disabled={!isAdmin} onChange={(v) => set('colorTint', v)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="營運模式"><Select value={f.franchiseType} onChange={(v) => set('franchiseType', v)} options={FRANCHISE} disabled={!isAdmin} /></Field>
              <Field label="加盟分潤 %"><input type="number" className="num" style={inputStyle} value={f.royaltyPct} disabled={!isAdmin} onChange={(e) => set('royaltyPct', Number(e.target.value) || 0)} /></Field>
            </div>
            {isAdmin ? (
              <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} disabled={save.isPending} onClick={() => save.mutate()}><Icon d={I.check} size={13} /> 儲存並套用</button>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>僅租戶管理者可編輯設定。</div>
            )}
          </div>
        </Card>

        <Card title="即時預覽" icon={<Icon d={I.eye} size={14} style={{ color: 'var(--primary)' }} />}>
          <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)' }}>
            <div style={{ background: f.color, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{f.brand.slice(0, 1)}</span>
              <b>{f.brand}</b>
            </div>
            <div style={{ padding: 16, background: f.colorTint }}>
              <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>按鈕與標籤預覽</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ background: f.color, color: '#fff', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>主要按鈕</span>
                <span style={{ background: f.colorSoft, color: '#fff', padding: '6px 14px', borderRadius: 8, fontSize: 12 }}>次要</span>
                <span style={{ border: `1px solid ${f.color}`, color: f.color, padding: '5px 12px', borderRadius: 999, fontSize: 11 }}>標籤</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card
        title="LINE OA 串接"
        icon={<Icon d={I.line} size={14} style={{ color: '#06C755' }} />}
        right={f.lineConfigured ? <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>● 已串接</span> : <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>● 站內模式（未串接）</span>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 680 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            到 LINE Developers 後台,把下列 Webhook URL 填入頻道,並貼上 Channel secret / access token。送出後客服訊息將**真實雙向同步**(收訊靠 Webhook 簽章驗證,送訊走 Messaging API push)。
          </div>
          <Field label="Webhook URL（填入 LINE 後台）">
            <input style={{ ...inputStyle, fontFamily: 'var(--mono, monospace)', fontSize: 11.5 }} value={webhookUrl} readOnly onFocus={(e) => e.target.select()} />
          </Field>
          {isAdmin && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Channel secret"><input type="password" style={inputStyle} value={line.secret} onChange={(e) => setLine((s) => ({ ...s, secret: e.target.value }))} placeholder={f.lineConfigured ? '（已設定，留空不更動）' : '貼上 channel secret'} autoComplete="off" /></Field>
                <Field label="Channel access token"><input type="password" style={inputStyle} value={line.token} onChange={(e) => setLine((s) => ({ ...s, token: e.target.value }))} placeholder={f.lineConfigured ? '（已設定，留空不更動）' : '貼上 access token'} autoComplete="off" /></Field>
              </div>
              <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} disabled={(!line.secret && !line.token) || saveLine.isPending} onClick={() => saveLine.mutate()}><Icon d={I.check} size={13} /> 儲存 LINE 憑證</button>
            </>
          )}
        </div>
      </Card>

      {isAdmin && (
        <Card
          title="分店各自經營 LINE OA（可選）"
          icon={<Icon d={I.line} size={14} style={{ color: '#06C755' }} />}
        >
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
            加盟店 / 想自主經營的分店,可填自己的 LINE 頻道與專屬 Webhook URL，收訊與推播都走自家官方帳號;
            <b>留空則沿用上方集團共用頻道</b>。同一套系統同時支援「集團共用」與「分店各自經營」。
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(branches.data ?? []).map((b) => (
              <BranchLineRow key={b.id} tenantId={f.id} branch={b} onSaved={() => { ok(`${b.name} LINE 頻道已更新`); void qc.invalidateQueries({ queryKey: ['branches'] }); }} onError={fail} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

interface BranchLite {
  id: string;
  name: string;
  short: string;
  country: string;
  region: string;
  lineConfigured: boolean;
}

function BranchLineRow({ tenantId, branch, onSaved, onError }: { tenantId: string; branch: BranchLite; onSaved: () => void; onError: (e: unknown) => void }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const url = `${window.location.origin}/api/v1/line/webhook/${tenantId}/${branch.id}`;
  const save = useMutation({
    mutationFn: () => api.patch(`/catalog/branches/${branch.id}/line-channel`, { lineChannelSecret: secret, lineChannelToken: token }),
    onSuccess: () => { setSecret(''); setToken(''); setOpen(false); onSaved(); },
    onError,
  });
  return (
    <div style={{ border: '1px solid var(--line-soft)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 600 }}>
          {branch.name}
          <span style={{ marginLeft: 8, fontSize: 11, color: branch.lineConfigured ? 'var(--success)' : 'var(--ink-4)' }}>
            ● {branch.lineConfigured ? '分店自有頻道' : '集團共用'}
          </span>
        </div>
        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setOpen((v) => !v)}>{open ? '收合' : '設定'}</button>
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <Field label="此分店 Webhook URL（填入該分店的 LINE 後台）">
            <input style={{ ...inputStyle, fontFamily: 'var(--mono, monospace)', fontSize: 11 }} value={url} readOnly onFocus={(e) => e.target.select()} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Channel secret"><input type="password" style={inputStyle} value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={branch.lineConfigured ? '（已設定，留空不更動）' : '留空＝沿用集團'} autoComplete="off" /></Field>
            <Field label="Channel access token"><input type="password" style={inputStyle} value={token} onChange={(e) => setToken(e.target.value)} placeholder={branch.lineConfigured ? '（已設定，留空不更動）' : '留空＝沿用集團'} autoComplete="off" /></Field>
          </div>
          <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} disabled={(!secret && !token) || save.isPending} onClick={() => save.mutate()}>
            <Icon d={I.check} size={13} /> 儲存分店頻道
          </button>
        </div>
      )}
    </div>
  );
}

function ColorField({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="color" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ width: 38, height: 34, padding: 2, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)' }} />
        <input style={{ ...inputStyle, flex: 1 }} className="num" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Field>
  );
}
